import React, { useState } from "react";
import { MegamenuOverlay } from "../../MegamenuOverlay/MegamenuOverlay";
import { Collapsible } from "@arctheme-components/elements/Collapsible/Collapsible";
import { Link } from "@arctheme-components/elements/Link/Link";
import { useNavigationParse } from "@arctheme-hooks/useNavigationParse";
import { Image } from "@arctheme-components/elements/Image/Image";

export const Accordion = ({ settings, menuItems, id, state }) => {
  const [expandedSections, setExpandedSections] = useState({});

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Parse the navigation items from the menu data
  const parseNavItems = () => {
    if (!menuItems || !menuItems.subMenuItems || !menuItems.subMenuItems[0] || !menuItems.subMenuItems[0].links) {
      return [];
    }
    return menuItems.subMenuItems[0].links;
  };

  const navItems = parseNavItems();

  // Render featured content if available
  const renderFeaturedContent = (item) => {
    if (!settings.section.show_featured_articles) return null;
    
    const articlesByCategory = settings.section.category_articles?.filter(
      article => article.category === item.title_without_classes
    ) || [];

    if (articlesByCategory.length === 0) return null;
    
    return (
      <div className="pt-2 pb-4">
        <h4 className="font-medium mb-2">{settings.section.featured_content_title || "Featured"}</h4>
        
        {articlesByCategory.map((article, idx) => (
          <div key={`featured-${idx}`} className="flex mb-3 pb-3 border-b border-gray-200 last:border-0">
            {article.image && (
              <div className="mr-3" style={{ width: "60px", height: "60px" }}>
                <Image 
                  imageUrl={article.image}
                  classes="w-full h-full object-cover"
                  alt={article.title}
                />
              </div>
            )}
            <div>
              <h5 className="font-medium mb-1">{article.title}</h5>
              <a href={article.url} className="text-sm font-medium">
                {article.linkText || "Read More"}
              </a>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Recursively render menu items
  const renderNavItems = (items, level = 0) => {
    return items.map((item, index) => {
      const parsedTitle = useNavigationParse(item.title);
      const hasChildren = item.links && item.links.length > 0;
      const isExpanded = expandedSections[`${level}-${index}`];
      const sectionId = `${level}-${index}`;
      const indent = level * 12; // 12px indentation per level
      
      // Skip items with "title" class at level 0
      if (level === 0 && parsedTitle.classes && parsedTitle.classes.includes('title')) {
        return null;
      }
      
      return (
        <div 
          key={`nav-${level}-${index}`} 
          className="mobile-megamenu-item"
          style={{ paddingLeft: `${indent}px` }}
        >
          {hasChildren ? (
            <>
              <div 
                className="mobile-megamenu-toggle"
                onClick={() => toggleSection(sectionId)}
              >
                <span className={`${settings.section.link_style || 'b2'}`}>
                  {parsedTitle.text}
                </span>
                <i className={`icon ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}`}></i>
              </div>
              
              {isExpanded && (
                <div className="mobile-megamenu-content expanded pl-4">
                  {/* Render child items */}
                  {renderNavItems(item.links, level + 1)}
                  
                  {/* Render featured content for this category */}
                  {level === 0 && renderFeaturedContent(item)}
                </div>
              )}
            </>
          ) : (
            <Link 
              link={item.url} 
              classes={`block py-2 ${settings.section.link_style || 'b2'}`}
            >
              {parsedTitle.text}
            </Link>
          )}
        </div>
      );
    });
  };

  // Only render if this megamenu is active
  if (state !== id) {
    return null;
  }

  return (
    <>
      <MegamenuOverlay settings={settings} type={'darken'} />
      <div className="mobile-megamenu p-4 overflow-auto max-h-[80dvh]">
        {/* Render top-level navigation items */}
        {renderNavItems(navItems)}
      </div>
    </>
  );
};

export default Accordion;